import { normalizeCountryCode, normalizeOptionalCountryCode } from '@shared/utils'
import { z } from 'zod'

export const homeBannerLandingTypeSchema = z.enum(['WEB', 'DEEPLINK', 'NONE'])
export const homeBannerPlacementSchema = z.enum(['HOME', 'JP_PLAN', 'JP_TRIAL'])

export const homeBannerSchema = z
  .object({
    id: z.string(),
    identifier: z.string(),
    image_url: z.string().min(1),
    pc_image_url: z.string().min(1).nullish(),
    landing_type: homeBannerLandingTypeSchema,
    landing_url: z.string(),
    priority: z.coerce.number(),
    target_countries: z.array(z.string()).optional(),
  })
  .superRefine((banner, context) => {
    if (banner.landing_type !== 'NONE' && banner.landing_url.length === 0) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'landing_url is required for clickable banners',
        path: ['landing_url'],
      })
    }
  })
  .transform((banner) => ({
    id: banner.id,
    identifier: banner.identifier,
    imageUrl: banner.image_url,
    pcImageUrl: banner.pc_image_url,
    landingType: banner.landing_type,
    landingUrl: banner.landing_url,
    priority: banner.priority,
    targetCountries: banner.target_countries
      ?.map(normalizeOptionalCountryCode)
      .filter((countryCode): countryCode is string => Boolean(countryCode)),
  }))

export const homeBannersResponseSchema = z.object({
  banners: z.array(homeBannerSchema),
})

export type HomeBannerLandingType = z.infer<typeof homeBannerLandingTypeSchema>
export type HomeBannerPlacement = z.infer<typeof homeBannerPlacementSchema>
export type HomeBanner = z.infer<typeof homeBannerSchema>
export type HomeBannersResponse = z.infer<typeof homeBannersResponseSchema>

export const isHomeBannerVisibleForCountry = ({
  banner,
  countryCode,
}: {
  banner: HomeBanner
  countryCode: string | null | undefined
}): boolean => {
  if (banner.targetCountries === undefined) {
    return true
  }

  if (banner.targetCountries.length === 0) {
    return true
  }

  return banner.targetCountries.includes(normalizeCountryCode(countryCode))
}
